import { TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY } from '../contracts';
import { getSessionDataConfig } from './config';

type Chunk = {
  index: number;
  text: string;
  confidence: number;
  model: string;
  audio_info: { duration?: number; size: number | null; mime_type: string | null };
  created_at: string;
  timestamp_ms: number;
  timestamp_cumulative_ms: number;
  diarised_transcript?: unknown;
  diarisation_status?: 'pending' | 'completed' | 'unavailable' | null;
};
type Recording = {
  recording_id: string;
  start_date: string;
  chunks: Chunk[];
  issues: {
    type: 'MISSING_CHUNKS' | 'RETRIED_CHUNKS' | 'DUPLICATE_CHUNKS';
    message: string;
  }[];
};
export type TranscriptSnapshot = { session_id: string; recordings: Recording[] };

/** Speakers are settled once the service reports completed or unavailable. */
export const isTerminalDiarisation = (status: Chunk['diarisation_status']): boolean =>
  status != null && status !== 'pending';

/**
 * Identify speakers off still lands on the bare cache entry, which dual-write
 * and a stored backend payload can both fill with speaker turns. Drop them so
 * the pane cannot keep rendering labels after a non-diarised read. A stripped
 * payload proves the service diarised this chunk; keeping that as the status
 * lets an Identify-off read still tell an undiarisable session apart from a
 * diarised one.
 */
export function stripDiarisedTranscriptFromResponse<T extends TranscriptSnapshot>(
  snapshot: T
): T {
  if (
    !snapshot.recordings.some((recording) =>
      recording.chunks.some((chunk) => chunk.diarised_transcript != null)
    )
  )
    return snapshot;
  return {
    ...snapshot,
    recordings: snapshot.recordings.map((recording) => ({
      ...recording,
      chunks: recording.chunks.map((chunk) =>
        chunk.diarised_transcript != null
          ? {
              ...chunk,
              diarised_transcript: null,
              diarisation_status: chunk.diarisation_status ?? 'completed',
            }
          : chunk
      ),
    })),
  };
}

export function updateTranscriptCache<T extends TranscriptSnapshot>(
  sessionId: string,
  updater: (snapshot: T) => T,
  initial: T
): void {
  const client = getSessionDataConfig().getQueryClient();
  // Keep the bare and diarised entries coherent — streaming chunk writes
  // must land on whichever query the pane is reading. The bare entry must
  // not keep speaker turns: Identify-off reads that key.
  for (const diarise of [false, true]) {
    const key = diarise
      ? [TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY, sessionId, { diarise: true }]
      : [TRANSCRIPTION_SERVICE_TRANSCRIPTS_QUERY_KEY, sessionId];
    client.setQueryData<T>(key, (current) => {
      const next = updater(current ?? initial);
      return diarise ? next : stripDiarisedTranscriptFromResponse(next);
    });
  }
}

export function mergeTranscriptSnapshots<T extends TranscriptSnapshot>(
  previous: T | undefined,
  next: T | undefined
): T | undefined {
  if (!previous || !next || previous.session_id !== next.session_id) return next;
  let changed = false;
  const recordings = next.recordings.map((recording) => {
    const previousChunks =
      previous.recordings.find((item) => item.recording_id === recording.recording_id)
        ?.chunks ?? [];
    const previousByOffset = new Map(
      previousChunks.map((chunk) => [chunk.timestamp_ms, chunk])
    );
    const chunks = recording.chunks.map((chunk) => {
      const cached = previousByOffset.get(chunk.timestamp_ms);
      // A read that omits the status is as unsettled as a pending one: neither may
      // drop speakers SSE already settled. Speakers the read carries (an Identify
      // PATCH) and a newer snapshot still win.
      if (
        cached &&
        chunk.diarised_transcript == null &&
        !isTerminalDiarisation(chunk.diarisation_status) &&
        isTerminalDiarisation(cached.diarisation_status) &&
        Date.parse(cached.created_at) >= Date.parse(chunk.created_at)
      ) {
        changed = true;
        return {
          ...chunk,
          created_at: cached.created_at,
          diarised_transcript: cached.diarised_transcript,
          diarisation_status: cached.diarisation_status,
        };
      }
      return chunk;
    });
    const offsets = new Set(chunks.map((chunk) => chunk.timestamp_ms));
    const missing = previousChunks.filter((chunk) => !offsets.has(chunk.timestamp_ms));
    if (missing.length) changed = true;
    return { ...recording, chunks: [...chunks, ...missing] };
  });
  const ids = new Set(recordings.map((recording) => recording.recording_id));
  for (const recording of previous.recordings) {
    if (!ids.has(recording.recording_id) && recording.chunks.length) {
      recordings.push(recording);
      changed = true;
    }
  }
  return changed ? orderTranscriptChunks({ ...next, recordings }) : next;
}

export function orderTranscriptChunks<T extends TranscriptSnapshot>(snapshot: T): T {
  let offset = 0;
  return {
    ...snapshot,
    recordings: [...snapshot.recordings]
      .sort((a, b) => Date.parse(a.start_date) - Date.parse(b.start_date))
      .map((recording) => {
        const base = offset;
        offset += Math.max(
          0,
          ...recording.chunks.map(
            (chunk) => chunk.timestamp_ms + (chunk.audio_info.duration ?? 0)
          )
        );
        return {
          ...recording,
          chunks: [...recording.chunks]
            .sort((a, b) => a.timestamp_ms - b.timestamp_ms)
            .map((chunk) => ({
              ...chunk,
              timestamp_cumulative_ms: base + chunk.timestamp_ms,
            })),
        };
      }),
  };
}
